// "Keep the cursor in the window": during a match the pointer is captured (Pointer Lock) and the game
// draws and drives its own cursor, so a fast flick can never carry a click outside the game.
//
// Design — a pointer-device emulator, not a second input path. While locked, every real mouse/pointer
// event (they all arrive at the lock target with a frozen clientX/Y) is stopped in the window's capture
// phase and re-dispatched as a synthetic event of the same type, button and modifiers at the virtual
// position, on the element under it (document.elementFromPoint). So input.ts (canvas pointerdown/move,
// window pointerup), hud.ts (skill-slot pointerenter tooltips, shop pointerdown/click, minimap RMB) and
// matchView's buttons read the virtual position through the very `clientX/clientY` they already read —
// the event IS the single position source, and none of them know about the lock. What a real mouse gets
// from the browser and a synthetic one doesn't is emulated here:
//   · boundary events (pointer/mouse over, out, enter, leave), also re-checked every frame, since the DOM
//     under a still cursor changes (the shop opening, a tooltip hiding) — browsers do that too;
//   · :hover — the `vhover` class goes on the hovered chain (index.html mirrors its in-match :hover rules);
//   · click/auxclick/dblclick go to the common ancestor of the press and release targets, like the real ones;
//   · the cursor image: the computed CSS `cursor` under the virtual point picks the drawn variant, so the
//     attack/target/ally/shop states input.ts sets through stage[data-cursor] carry over unchanged.
// Responsiveness — the OS cursor is drawn by hardware, this one by the page, so a movement does as little
// as possible: it updates the position and one transform on the cursor's own compositor layer, nothing
// else. It arrives by pointerrawupdate where the browser has it (Chromium: dispatched as the mouse moves,
// while pointermove waits for the next frame), else by pointermove. Everything that reads layout — the
// stage rect, the hit test, boundary events, the computed cursor style — runs once a frame (update()),
// and a press/release/click hit-tests at the exact current point before it is dispatched. A forwarded
// pointermove goes to what was under the cursor at the last frame.
// Not forwarded: wheel (it lands on the lock target — the stage — whose zoom handler already swallows it,
// exactly as it does over the shop today) and keyboard (never position-bound).
// Esc: a browser spends the Esc press on leaving the lock and may not deliver it; when it drops the lock
// while the window still has focus, a synthetic Esc keydown follows, so the aim is cancelled and the shop
// closed as the player meant. Both handlers are idempotent, so a delivered Esc as well is harmless.
// The real cursor reappears where the OS left it — a page can't move it to the virtual position.
import { clampToRect, createLockMachine, createMovementScale, integrate, startPoint, type LockApi, type Point, type Rect } from './pointerLock.js';

const FORWARDED = ['pointermove', 'pointerdown', 'pointerup', 'mousemove', 'mousedown', 'mouseup', 'click', 'auxclick', 'dblclick', 'contextmenu'] as const;
const EVENT_KEYS = ['button', 'buttons', 'ctrlKey', 'shiftKey', 'altKey', 'metaKey', 'detail', 'pointerId', 'pointerType', 'isPrimary', 'pressure', 'width', 'height'] as const;

/** The drawn variants, keyed by the CSS cursor they stand for. */
export type CursorKind = 'arrow' | 'attack' | 'target' | 'pointer' | 'blocked';
export function cursorKind(css: string): CursorKind {
  if (css === 'crosshair') return 'attack';
  if (css === 'cell') return 'target';
  if (css === 'pointer') return 'pointer';
  if (css === 'not-allowed') return 'blocked';
  return 'arrow';
}

// Engraving-style pointers: sepia ink over the paper tone, hatched like the plates. Hotspot at the tip
// for the arrows, the centre for the reticles (see HOTSPOT).
const INK = '#2c1e12', PAPER = '#efe3c6', CARMINE = '#a8322a', GOLD = '#d6a85a';
const ARROW = 'M2 2 L2 24 L7.8 18.8 L11.6 27.6 L15.6 25.8 L11.8 17.2 L19.4 17.2 Z';
// Every stroke sits on a wider paper-tone halo, so the cursor reads on dark buttons and dense hatching alike.
const halo = (d: string, w: number) => `<path d="${d}" fill="none" stroke="${PAPER}" stroke-width="${w}" stroke-linejoin="round" stroke-linecap="round"/>`;
const arrow = (fill: string, hatch: string) => `${halo(ARROW, 4)}<path d="${ARROW}" fill="${fill}" stroke="${INK}" stroke-width="1.6" stroke-linejoin="round"/>
    <path d="M3.6 9 L7.4 5.2 M3.6 13.4 L10 7 M3.6 17.8 L12.6 8.8 M5.4 20.4 L15.4 10.4 M9 21.2 L17.4 12.8" stroke="${hatch}" stroke-width=".8" clip-path="url(#vcur-clip)"/>`;
const TICKS = 'M16 2 V9 M16 23 V30 M2 16 H9 M23 16 H30';
const SVG: Record<CursorKind, string> = {
  arrow: arrow(PAPER, INK),
  pointer: arrow(GOLD, INK),
  blocked: `${arrow(PAPER, INK)}<circle cx="22" cy="24" r="5.4" fill="${PAPER}" stroke="${CARMINE}" stroke-width="2"/><path d="M18.2 27.8 L25.8 20.2" stroke="${CARMINE}" stroke-width="2"/>`,
  attack: `<g fill="none" stroke-linecap="round">
    <circle cx="16" cy="16" r="9" stroke="${PAPER}" stroke-width="5"/>${halo(TICKS, 5)}
    <circle cx="16" cy="16" r="9" stroke="${CARMINE}" stroke-width="2.2"/>
    <path d="${TICKS}" stroke="${INK}" stroke-width="2"/>
    <circle cx="16" cy="16" r="1.8" fill="${CARMINE}" stroke="${PAPER}" stroke-width="1"/></g>`,
  target: `<g fill="none" stroke-linecap="round">
    <circle cx="16" cy="16" r="11" stroke="${PAPER}" stroke-width="5"/>
    <circle cx="16" cy="16" r="11" stroke="${INK}" stroke-width="1.8"/>
    <circle cx="16" cy="16" r="5.5" stroke="${INK}" stroke-width="1.2" stroke-dasharray="2.2 1.8"/>
    ${halo('M16 5 V10 M16 22 V27 M5 16 H10 M22 16 H27', 3.6)}<path d="M16 5 V10 M16 22 V27 M5 16 H10 M22 16 H27" stroke="${INK}" stroke-width="1.6"/>
    <circle cx="16" cy="16" r="1.8" fill="${GOLD}" stroke="${INK}" stroke-width=".8"/></g>`,
};
const HOTSPOT: Record<CursorKind, Point> = { arrow: { x: 2, y: 2 }, pointer: { x: 2, y: 2 }, blocked: { x: 2, y: 2 }, attack: { x: 16, y: 16 }, target: { x: 16, y: 16 } };

type Deps = {
  stageEl: HTMLElement;
  api: LockApi;
  /** The player's «Удерживать курсор в окне». */
  enabled: boolean;
  win?: Window;
  doc?: Document;
  /** What is under a client point; elementFromPoint by default (tests inject a layout). */
  pick?: (x: number, y: number) => Element | null;
  /** Movement from clientX/Y deltas instead of movementX/Y: the ?vcursor dev lock, where the real
      cursor is never actually captured. */
  deltaFromClient?: boolean;
  /** The lock machine's clock (ms); performance.now by default. */
  now?: () => number;
  /** ?vcursor-stats: record per move event its input-to-draw latency and handler time (stats()). */
  stats?: boolean;
};

const STATS_KEEP = 4000;
const summary = (a: number[]) => {
  const s = [...a].sort((x, y) => x - y), q = (p: number) => s[Math.min(s.length - 1, Math.floor(p * s.length))] ?? 0;
  return { n: s.length, p50: q(0.5), p95: q(0.95), max: s.at(-1) ?? 0 };
};

export function createVirtualPointer({ stageEl, api, enabled, win = window, doc = document, pick, deltaFromClient = false, now, stats = false }: Deps) {
  const at = pick ?? ((x: number, y: number) => doc.elementFromPoint(x, y));
  const scale = createMovementScale();
  const mine = new WeakSet<Event>();  // our own synthetic events pass the capture filter untouched
  let pos: Point = { x: 0, y: 0 }, lastReal: Point | null = null;
  // The previous unlocked move, for calibration only: dropped when the mouse leaves the window, since the
  // next move's clientX jump then spans travel (another monitor) its movementX never saw.
  let prevSample: Point | null = null;
  // The mouse buttons held, from the last real event: synthetic moves carry them, or input.ts would read a
  // held LMB (a charging shot) as released.
  let buttons = 0;
  let hovered: Element | null = null, pressed: Element | null = null;
  // The element the last synthetic pointermove went to: a hover it hasn't had a move on yet (entered by
  // a frame's check, or by a press's own hit test) gets one at the next frame.
  let movedTo: EventTarget | null = null;
  let kind: CursorKind | null = null, hotspot = HOTSPOT.arrow;
  // The stage's rect as of the last frame: a move clamps against it instead of reading layout per event.
  let stage: Rect = { left: 0, top: 0, width: 0, height: 0 };
  // pointerrawupdate (Chromium) arrives as the mouse moves; pointermove is held to the next frame and
  // carries the sum of the movement since the previous one. So pointermove stays the authority — the
  // position is `anchor` (where the last pointermove left it) plus its movement, clamped to the stage —
  // and in between the cursor shows a preview: anchor plus the rawupdates since, clamped the same way, so
  // a flick into the edge and back previews exactly where the pointermove will settle. A rawupdate the
  // browser drops or delivers out of order (seen in Chrome right after the listener is added) only skews
  // the preview until the next pointermove; no movement is counted twice.
  const rawAvailable = 'onpointerrawupdate' in win;
  let anchor: Point = { x: 0, y: 0 }, rawClient: Point | null = null, rawSum: Point = { x: 0, y: 0 };
  const probe = { pointerrawupdate: { lat: [] as number[], work: [] as number[] }, pointermove: { lat: [] as number[], work: [] as number[] } };

  // Its own compositor layer at the top of the page, outside the WebGL stage (position: fixed, see
  // index.html): a move rewrites one transform and nothing repaints with the canvas.
  const el = doc.createElement('div');
  el.className = 'vcursor';
  el.hidden = true;
  el.setAttribute('aria-hidden', 'true');
  (doc.body ?? stageEl).appendChild(el);

  const rect = (): Rect => stageEl.getBoundingClientRect();

  function fire(type: string, target: EventTarget, src: Partial<MouseEvent & PointerEvent> | null, extra: Record<string, unknown> = {}) {
    const bubbles = !type.endsWith('enter') && !type.endsWith('leave');
    const init: Record<string, unknown> = { bubbles, cancelable: bubbles, composed: true, view: win, clientX: pos.x, clientY: pos.y, screenX: pos.x, screenY: pos.y };
    for (const k of EVENT_KEYS) if (src && src[k] !== undefined) init[k] = src[k];
    if (type.startsWith('pointer')) { init.pointerId ??= 1; init.pointerType ??= 'mouse'; init.isPrimary ??= true; }
    Object.assign(init, extra);
    const w = win as unknown as Record<string, (new (t: string, i: object) => Event) | undefined>;
    const Ctor = (type.startsWith('pointer') ? w.PointerEvent : undefined) ?? w.MouseEvent ?? w.Event!;
    const ev = new Ctor(type, init);
    // A bare Event (DOM-less tests) takes no mouse fields from its init.
    for (const [k, v] of Object.entries(init)) if (k !== 'view' && (ev as unknown as Record<string, unknown>)[k] !== v) {
      try { Object.defineProperty(ev, k, { value: v }); } catch { /* read-only on this engine */ }
    }
    mine.add(ev);
    if (type === 'pointermove') movedTo = target;
    target.dispatchEvent(ev);
    return ev;
  }

  const chain = (e: Element | null) => { const out: Element[] = []; for (let n = e; n; n = n.parentElement) out.push(n); return out; };

  /** Moves the hover to `next`, with the boundary events and the :hover stand-in a real move would bring. */
  function retarget(next: Element | null, src: Partial<MouseEvent & PointerEvent> | null) {
    if (next === hovered) return;
    const prev = hovered;
    hovered = next;
    const before = chain(prev), after = chain(next);
    const left = before.filter(n => !after.includes(n)), entered = after.filter(n => !before.includes(n)).reverse();
    for (const p of ['pointer', 'mouse'] as const) {
      if (prev) fire(`${p}out`, prev, src, { relatedTarget: next });
      for (const n of left) fire(`${p}leave`, n, src, { relatedTarget: next });
      if (next) fire(`${p}over`, next, src, { relatedTarget: prev });
      for (const n of entered) fire(`${p}enter`, n, src, { relatedTarget: prev });
    }
    for (const n of left) n.classList.remove('vhover');
    for (const n of entered) n.classList.add('vhover');
  }

  /** A hit test at the exact current point (layout may be read here: presses, frames, never plain moves). */
  const under = () => at(pos.x, pos.y) ?? stageEl;

  /** The cursor image for what is hovered (reads computed style: once a frame). */
  function restyle() {
    const next = cursorKind(hovered ? win.getComputedStyle?.(hovered).cursor ?? '' : '');
    if (next === kind) return;
    kind = next;
    hotspot = HOTSPOT[next];
    el.dataset.kind = next;
    el.innerHTML = `<svg width="32" height="32" viewBox="0 0 32 32"><defs><clipPath id="vcur-clip"><path d="${ARROW}"/></clipPath></defs>${SVG[next]}</svg>`;
  }
  /** The only per-move DOM write. */
  function place() {
    el.style.transform = `translate3d(${pos.x - hotspot.x}px, ${pos.y - hotspot.y}px, 0)`;
  }
  /** Once a frame (and when the lock starts): layout reads, hit test, boundary events; the cursor image
      only when asked (`shape`: the lock's start and the dev hook — a frame restyles in shape(), after
      input.ts). The element under the cursor also gets a move at the current point if its last one went
      elsewhere, as a browser sends after over/enter: the move that crossed was forwarded to the old hover,
      or a press retargeted without one (input.ts's canvas would otherwise think the pointer is still
      outside until the next movement). True when that move went out. */
  function sync(src: Partial<MouseEvent & PointerEvent>, shape = false) {
    stage = rect();
    pos = clampToRect(pos, stage);  // the window may have been resized under a still cursor
    anchor = clampToRect(anchor, stage);
    retarget(under(), src);
    if (shape) restyle();
    place();
    if (!hovered || hovered === movedTo) return false;
    fire('pointermove', hovered, src, { movementX: 0, movementY: 0 });
    fire('mousemove', hovered, src, { movementX: 0, movementY: 0 });
    return true;
  }
  /** One movement event: position and the transform, nothing else. A pointermove settles the position
      from `anchor`; a rawupdate moves ahead of it. */
  function step(m: MouseEvent, raw: boolean) {
    const t0 = stats ? performance.now() : 0;
    let dx = m.movementX, dy = m.movementY;
    if (deltaFromClient) {  // dev lock: the real cursor moves, each stream measured from its own last point
      const last = (raw ? rawClient : lastReal) ?? { x: m.clientX, y: m.clientY };
      dx = m.clientX - last.x; dy = m.clientY - last.y;
      if (raw) rawClient = { x: m.clientX, y: m.clientY }; else lastReal = rawClient = { x: m.clientX, y: m.clientY };
    }
    const k = deltaFromClient ? 1 : scale.value;
    if (raw) {
      rawSum = { x: rawSum.x + dx * k, y: rawSum.y + dy * k };
      pos = integrate(anchor, rawSum.x, rawSum.y, 1, stage);
    } else {
      anchor = pos = integrate(anchor, dx, dy, k, stage);
      rawSum = { x: 0, y: 0 };
    }
    place();
    if (stats) {
      const t1 = performance.now(), p = probe[raw ? 'pointerrawupdate' : 'pointermove'];
      p.lat.push(t1 - m.timeStamp); p.work.push(t1 - t0);
      if (p.lat.length > STATS_KEEP) { p.lat.shift(); p.work.shift(); }
    }
  }

  const onRaw = (e: Event) => {
    const m = e as PointerEvent;
    if (machine.state !== 'locked' || !fromMouse(m)) return;
    e.stopImmediatePropagation();
    step(m, true);
  };

  const machine = createLockMachine(api, stageEl, {
    enabled, now,
    onLock() {
      stage = rect();
      anchor = pos = startPoint(lastReal, stage);
      rawClient = lastReal;
      rawSum = { x: 0, y: 0 };
      el.hidden = false;
      stageEl.classList.add('vcursor-on');
      if (rawAvailable) win.addEventListener('pointerrawupdate', onRaw, true);
      movedTo = null;
      sync({ buttons }, true);  // the first hover brings its move
    },
    onUnlock(forced) {
      if (rawAvailable) win.removeEventListener('pointerrawupdate', onRaw, true);
      retarget(null, { buttons });
      pressed = null;
      el.hidden = true;
      stageEl.classList.remove('vcursor-on');
      if (forced && (doc.hasFocus?.() ?? true)) {
        const w = win as unknown as Record<string, (new (t: string, i: object) => Event) | undefined>;
        const Key = w.KeyboardEvent ?? w.Event!;
        const esc = new Key('keydown', { key: 'Escape', code: 'Escape', bubbles: true, cancelable: true });
        if ((esc as KeyboardEvent).code !== 'Escape') Object.defineProperty(esc, 'code', { value: 'Escape' });
        win.dispatchEvent(esc);
      }
    },
  });

  /* A pointer event says its device; a compatibility mouse event made from a touch says so through
     sourceCapabilities (Chromium). */
  const fromMouse = (m: MouseEvent & Partial<PointerEvent>) =>
    (m.pointerType ?? 'mouse') === 'mouse' && !(m as { sourceCapabilities?: { firesTouchEvents?: boolean } }).sourceCapabilities?.firesTouchEvents;

  const onEvent = (e: Event) => {
    if (mine.has(e)) return;
    const m = e as MouseEvent & PointerEvent;
    if (typeof m.buttons === 'number' && fromMouse(m)) buttons = m.buttons;
    if (machine.state !== 'locked') {
      if (e.type === 'pointermove') {
        if (prevSample) scale.sample(m.movementX, m.movementY, m.clientX - prevSample.x, m.clientY - prevSample.y);
        lastReal = prevSample = { x: m.clientX, y: m.clientY };
      } else if (e.type === 'pointerdown') {
        lastReal = { x: m.clientX, y: m.clientY };
        // A mouse only: a touch or pen press has no cursor to keep in the window.
        if (fromMouse(m) && stageEl.contains(e.target as Node | null)) machine.press();
      }
      return;
    }
    // Touch and pen keep working at their real point: the lock captures only the mouse.
    if (!fromMouse(m)) return;
    e.stopImmediatePropagation();
    if (e.type === 'contextmenu') e.preventDefault();
    if (e.type === 'pointermove' || e.type === 'mousemove') {
      if (e.type === 'pointermove') step(m, false);
      // To what was under the cursor at the last frame: the hover itself moves on once a frame (sync).
      fire(e.type, hovered ?? stageEl, m, { movementX: m.movementX, movementY: m.movementY });
      return;
    }
    // A press, release or click lands exactly where the cursor is now, hit-tested on the spot.
    const target = under();
    retarget(target, m);
    let to: Element = target;
    if (e.type === 'pointerdown' || e.type === 'mousedown') pressed = target;
    else if (e.type === 'click' || e.type === 'auxclick' || e.type === 'dblclick') {
      const from = chain(pressed);
      to = chain(target).find(n => from.includes(n)) ?? target;
    }
    const out = fire(e.type, to, m);
    if (out.defaultPrevented) e.preventDefault();
  };
  for (const type of FORWARDED) win.addEventListener(type, onEvent, true);
  const onLeave = (e: Event) => { if (!(e as MouseEvent).relatedTarget) prevSample = null; };  // out of the window
  const onBlur = () => { prevSample = null; };
  const onResize = () => { prevSample = null; scale.reset(); };  // page zoom changes the ratio
  win.addEventListener('mouseout', onLeave);
  win.addEventListener('blur', onBlur);
  win.addEventListener('resize', onResize);

  return {
    get locked() { return machine.state === 'locked'; },
    get position(): Point { return { ...pos }; },
    /** Per frame: whether the match wants the lock (live, not ended); re-checks what is under the cursor.
        The cursor image follows in shape(), once input.ts has had the frame. */
    update(active: boolean) {
      machine.setActive(active);
      if (machine.state === 'locked') sync({ buttons });
    },
    /** After input.ts has set this frame's stage[data-cursor]: the cursor image follows in the same frame. */
    shape() {
      if (machine.state !== 'locked') return;
      restyle();
      place();
    },
    /** Dev/test hook: a locked move by (dx, dy) CSS px, as if the mouse moved and a frame passed. */
    move(dx: number, dy: number) {
      if (machine.state !== 'locked') return;
      anchor = pos = integrate(pos, dx, dy, 1, stage);
      rawSum = { x: 0, y: 0 };
      if (!sync({ buttons }, true)) fire('pointermove', hovered ?? stageEl, { buttons }, { movementX: dx, movementY: dy });
    },
    /** ?vcursor-stats: per event type, ms from the event's timeStamp to its transform write (latency)
        and the time spent handling it (handler). */
    stats() {
      const of = (p: { lat: number[]; work: number[] }) => ({ latency: summary(p.lat), handler: summary(p.work) });
      return { rawupdateAvailable: rawAvailable, pointerrawupdate: of(probe.pointerrawupdate), pointermove: of(probe.pointermove) };
    },
    destroy() {
      machine.destroy();
      for (const type of FORWARDED) win.removeEventListener(type, onEvent, true);
      if (rawAvailable) win.removeEventListener('pointerrawupdate', onRaw, true);
      win.removeEventListener('mouseout', onLeave);
      win.removeEventListener('blur', onBlur);
      win.removeEventListener('resize', onResize);
      el.remove();
      stageEl.classList.remove('vcursor-on');
    },
  };
}
